// Who a player *is* on the board, chosen in the lobby and carried in the game state.
// Twenty icons picked to be told apart across a room: no two from the same family, no
// two that share a silhouette, and none that collapse into the same blob at avatar
// size. Ten colours pull the seats further apart again, so a full table reads at a
// glance instead of as eight identical cream cards.
//
// Ids are ascii keys because they travel in the synced blob and land in class names;
// labels are what the picker shows, so those are Swedish like the rest of the game.
export const PLAYER_ICONS = [
  {id: "fox", symbol: "🦊", label: "Räv"},
  {id: "panda", symbol: "🐼", label: "Panda"},
  {id: "frog", symbol: "🐸", label: "Groda"},
  {id: "owl", symbol: "🦉", label: "Uggla"},
  {id: "octopus", symbol: "🐙", label: "Bläckfisk"},
  {id: "unicorn", symbol: "🦄", label: "Enhörning"},
  {id: "bee", symbol: "🐝", label: "Bi"},
  {id: "dino", symbol: "🦖", label: "Dinosaurie"},
  {id: "mushroom", symbol: "🍄", label: "Svamp"},
  {id: "cactus", symbol: "🌵", label: "Kaktus"},
  {id: "pizza", symbol: "🍕", label: "Pizza"},
  {id: "donut", symbol: "🍩", label: "Munk"},
  {id: "bolt", symbol: "⚡", label: "Blixt"},
  {id: "fire", symbol: "🔥", label: "Eld"},
  {id: "wave", symbol: "🌊", label: "Våg"},
  {id: "guitar", symbol: "🎸", label: "Gitarr"},
  {id: "rocket", symbol: "🚀", label: "Raket"},
  {id: "crown", symbol: "👑", label: "Krona"},
  {id: "skull", symbol: "💀", label: "Dödskalle"},
  {id: "dice", symbol: "🎲", label: "Tärning"},
];

// Dark enough that white sits on them legibly, and spread around the wheel so two
// seats never end up as neighbouring shades of the same colour.
export const PLAYER_COLORS = [
  {id: "coral", hex: "#d9542f", label: "Korall"},
  {id: "amber", hex: "#c2860f", label: "Bärnsten"},
  {id: "lime", hex: "#6f9d1e", label: "Lime"},
  {id: "forest", hex: "#2b8a55", label: "Skog"},
  {id: "teal", hex: "#10868b", label: "Turkos"},
  {id: "sky", hex: "#2b72c4", label: "Himmel"},
  {id: "indigo", hex: "#5348c0", label: "Indigo"},
  {id: "violet", hex: "#8a3fbe", label: "Violett"},
  {id: "rose", hex: "#c93a74", label: "Ros"},
  {id: "slate", hex: "#5c6672", label: "Skiffer"},
];

// Everyone who never opened the picker — every bot, every player from before this
// existed — still has to look like someone. Hashing the player id gives each of them
// a stable identity instead of one shared default, and costs nothing to sync.
function seedIndex(seed, length) {
  const text = String(seed);
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 1000003;
  return hash % length;
}

// The salt is what keeps the two picks independent. Hashing both off the same number
// made the colour index the icon index modulo ten, so the grid only ever held twenty
// looks and a lobby of bots turned up wearing the same blue unicorn twice.
function normalizeId(value, seed, options, salt) {
  const match = options.find((option) => option.id === value);
  return match ? match.id : options[seedIndex(`${seed}${salt}`, options.length)].id;
}

// The trust boundary. Both ids arrive inside a blob any device with the lobby code can
// POST, and both are painted: the icon as a symbol, the colour as a hex in a style
// attribute. Allow-listing is what keeps them harmless — every function here answers
// with one of our own values and never with the caller's string.
export function normalizeIconId(value, seed = "") {
  return normalizeId(value, seed, PLAYER_ICONS, "#icon");
}

export function normalizeColorId(value, seed = "") {
  return normalizeId(value, seed, PLAYER_COLORS, "#color");
}

export function iconSymbol(id) {
  return (PLAYER_ICONS.find((icon) => icon.id === id) || PLAYER_ICONS[0]).symbol;
}

export function colorHex(id) {
  return (PLAYER_COLORS.find((color) => color.id === id) || PLAYER_COLORS[0]).hex;
}
